import type { AreaDto } from './areas.js';
import { z } from 'zod';
import { DAY_PATTERN, isPlausibleDay, normalizeColor, type ColorValue, PROJECT_HEALTH, PROJECT_KEY_PATTERN, PROJECT_PHASES, type ProjectPhase, PROJECT_MEMBER_ROLES, PROJECT_ROLES, PROJECT_TOOLS, STATUS_CATEGORIES, TASK_TITLE_MAX, BULK_MAX, REPEAT_UNITS, REPEAT_BASES, type Repeat, SHARING_MODES, type SharingMode, WIP_LIMIT_MAX, type ProjectHealth, type ProjectTool, type GuestAccess } from '@werft/domain';
import { nameSchema, opIdSchema, patchOf, uuidSchema } from './common.js';
import type { CustomFieldDto, FieldValue } from './fields.js';
import { memberProfileSchema, type FormerMemberDto } from './guests.js';
import { OVERVIEW_CARDS, type OverviewCard } from './signals.js';

/** W03/W04: Darstellung – ein-/ausblendbare Angaben (Warnungen wie überfällig/wartet bleiben immer sichtbar) */
export const DISPLAY_PROPS = ['due', 'people', 'labels', 'progress', 'activity', 'cover'] as const;
export type DisplayProp = (typeof DISPLAY_PROPS)[number];
export const BOARD_MODES = ['compact', 'normal', 'visual'] as const;
export type BoardMode = (typeof BOARD_MODES)[number];
export interface ListDisplay {
  density: 'compact' | 'comfortable';
  /** Titel mehrzeilig statt abgeschnitten */
  wrap: boolean;
  props: DisplayProp[];
}
export interface BoardDisplay {
  mode: BoardMode;
  props: DisplayProp[];
}
export interface ProjectDisplayDto {
  list: ListDisplay;
  board: BoardDisplay;
}
const displayPropsSchema = z.array(z.enum(DISPLAY_PROPS)).max(DISPLAY_PROPS.length);
export const listDisplaySchema = z.object({ density: z.enum(['compact', 'comfortable']), wrap: z.boolean(), props: displayPropsSchema });
export const boardDisplaySchema = z.object({ mode: z.enum(BOARD_MODES), props: displayPropsSchema });
/** Ausgangszustand ohne Projektvorgabe: wie bisher (großzügig, alles außer Titelbild in der Liste) */
export const DEFAULT_DISPLAY: ProjectDisplayDto = {
  list: { density: 'comfortable', wrap: false, props: ['due', 'people', 'labels', 'progress', 'activity'] },
  board: { mode: 'normal', props: ['due', 'people', 'labels', 'progress', 'activity', 'cover'] },
};

/** Mögliche Startseiten eines Projekts (Audit F07): Aufgaben oder ein Werkzeug */
export const PROJECT_HOMES = ['tasks', ...PROJECT_TOOLS] as const;
export type ProjectHome = (typeof PROJECT_HOMES)[number];

export const projectRoleSchema = z.enum(PROJECT_ROLES);
export const daySchema = z.string().regex(DAY_PATTERN).refine(isPlausibleDay, 'Ungültiges Datum');
export const colorSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** ADR 0134: fachlicher Farbwert – Palettenname oder eigene Farbe; HEX wird zu `#RRGGBB` normalisiert */
export const colorValueSchema = z
  .string()
  .max(16)
  .transform((s, ctx) => {
    const c = normalizeColor(s);
    if (!c) {
      ctx.addIssue({ code: 'custom', message: 'Farbe: Palettenname oder sechsstelliger HEX-Wert (#RRGGBB)' });
      return z.NEVER;
    }
    return c;
  });

/* ---------- Projekte ---------- */

/** W1 (ADR 0070): Vorschau beim Zuschalten – wer das Werkzeug sieht (nur Zahlen; Namen nur derer, die es nicht sehen) */
export interface ToolAudienceDto {
  tool: ProjectTool;
  audience: 'all' | 'material' | 'members' | 'editors';
  sensitive: boolean;
  sees: { leads: number; members: number; guests: number; linkGuests: number };
  hidden: { members: number; guests: number; linkGuests: number };
  /** Gäste, die im Werkzeug nur Inhalte ihrer Bereiche oder Aufgaben sehen */
  scoped: number;
  hiddenNames: string[];
}

export interface ProjectDto {
  id: string;
  workspaceId: string;
  name: string;
  /** P25: Kürzel für Aufgaben-Schlüssel (KURS-42), im Workspace eindeutig */
  key: string;
  color: string | null;
  archived: boolean;
  version: number;
  /** Eigene Rolle der anfragenden Person */
  role: z.infer<typeof projectRoleSchema>;
  /** Nur für Gäste (P15): eigene Stufe und ob „Allgemein“ (ganzes Projekt) freigegeben ist */
  guest?: { access: GuestAccess; wholeProject: boolean; tasksOnly: boolean; onlyAssigned: boolean; ownCreated?: boolean; material?: boolean; until?: string | null };
  /** ADR 0063: eingeschränktes Mitglied – eigene Stufe/Umfang (fehlt = uneingeschränkt) */
  limits?: { access: GuestAccess; onlyAssigned: boolean; ownCreated: boolean; material: boolean; intake?: 'all' | 'assigned' };
  /** P21: Vorschau „Als Gast ansehen“ (Projektleitung) – nur lesen */
  preview?: boolean;
  createdAt: string;
  /** Zugeschaltete Werkzeuge über die Aufgaben hinaus (P9); leer = schlichter Start */
  tools: ProjectTool[];
  /** Startseite: Aufgaben (Standard, Regel R3) oder ein zugeschaltetes Werkzeug (Audit F07: ein Dokumentprojekt
   *  beginnt in den Notizen) */
  home: ProjectHome;
  /** W07: Reihenfolge der Werkzeugleiste (zugeschaltete Werkzeuge inkl. Aufgaben); Startseite ist davon getrennt */
  order: ProjectHome[];
  /** W03/W04: Darstellungsvorgabe der Projektleitung für Liste und Board (persönliche Abweichung im Browser) */
  display: ProjectDisplayDto;
  /** W10: Notfallsperre externer Zugriffe (nur für die Projektleitung sichtbar, sonst null) */
  externalLock: { by: string | null; at: string; reason: string } | null;
  /** Audit F07: kurzer Zweck des Projekts (Übersicht), null = nicht gepflegt */
  purpose: string | null;
  /** 0.155 (ADR 0157): eingestellte Karten der Übersicht (null = Vorauswahl) */
  overviewCards?: OverviewCard[] | null;
  /** Projektstatus (Übersicht), null = nicht gesetzt */
  health: ProjectHealthDto | null;
  /** P17: als Vorlage im Workspace angeboten */
  template: boolean;
  /** ADR 0104: Entwurf einer Vorlage – zuletzt veröffentlichte Fassung (null = nicht veröffentlicht) */
  templateRelease?: { version: number; publishedAt: string } | null;
  /** P20: wer Freigabelinks erzeugen darf */
  sharing: SharingMode;
  /** P24: Phase und geplanter Zeitraum (Tage, null = offen) */
  phase: ProjectPhase;
  startDate: string | null;
  endDate: string | null;
  /** Vorlagen (ADR 0062): ruhiger Hinweis auf den ersten sinnvollen Schritt; Stichtage/Rollen/Bausteine vorhanden */
  firstStep?: string | null;
  planned?: boolean;
  /** ADR 0093: Ausgabe einer Projektserie (Lobby zeigt die Serie, das Projekt selbst bleibt eine Insel) */
  series?: { id: string; edition: number } | null;
  /** ADR 0087: vorgeschlagene Stufen für Freigabeverfahren (Personen aus den Rollen der Vorlage) */
  approvalPreset?: { name: string; mode: 'all' | 'any'; approverIds: string[] }[] | null;
}

export interface ProjectHealthDto {
  kind: ProjectHealth;
  note: string | null;
  by: string | null;
  at: string;
}

/** Projektkarte in der Lobby */
export interface ProjectCardDto extends ProjectDto {
  openTasks: number;
  doneTasks: number;
  lastActivityAt: string;
  /** P24: offene Aufgaben mit Termin vor heute (sichtbare) */
  overdueTasks: number;
  /** P24: nächster offener Meilenstein (sichtbar), sonst null */
  nextMilestone: { id: string; title: string; dueDate: string } | null;
  /** P24: Projektleitung (für Filter und Anzeige) */
  leads: { userId: string; name: string }[];
  /** ADR 0122: persönlicher Favorit */
  favorite: boolean;
  /** ADR 0122: eigene Teams, die in diesem Projekt arbeiten (für den Teamfilter) – nie fremde Teams */
  teams: { id: string; name: string }[];
  /** ADR 0124: persönlicher Lobby-Ordner (null = oberste Ebene) */
  folderId: string | null;
}

/** P24: Zeitachse der Lobby – je Projekt Spanne der Termine und Meilensteine (nur Sichtbares) */
export interface PortfolioRowDto {
  projectId: string;
  /** frühester/spätester Start- oder Fälligkeitstag sichtbarer Aufgaben */
  first: string | null;
  last: string | null;
  milestones: { id: string; title: string; dueDate: string; done: boolean }[];
}
export interface PortfolioDto {
  /** „Heute“ in der Zeitzone der Instanz */
  today: string;
  items: PortfolioRowDto[];
}

/** Schlichter Einstieg (Briefing, Ablauf A): nur der Name ist Pflicht */
export const createProjectInput = z.object({
  opId: opIdSchema,
  name: nameSchema,
  color: colorSchema.optional(),
});
export type CreateProjectInput = z.infer<typeof createProjectInput>;

export const updateProjectInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({
    name: nameSchema,
    color: colorSchema.nullable(),
    archived: z.boolean(),
    /** P9: Werkzeuge (Reihenfolge = Anzeige), Startseite, Status */
    tools: z.array(z.enum(PROJECT_TOOLS)).max(PROJECT_TOOLS.length),
    home: z.enum(PROJECT_HOMES),
    /** W07: Reihenfolge der Werkzeugleiste (inkl. 'tasks'); nicht Zugeschaltetes wird ignoriert */
    order: z.array(z.enum(PROJECT_HOMES)).max(PROJECT_HOMES.length),
    /** W03/W04: Darstellungsvorgabe (null = zurück zum Ausgangszustand) */
    display: z.object({ list: listDisplaySchema, board: boardDisplaySchema }).nullable(),
    purpose: z.string().trim().max(280).nullable(),
    /** 0.155 (ADR 0157): Karten der Übersicht (Reihenfolge = Anzeige; null = Vorauswahl aus den Werkzeugen) */
    overviewCards: z.array(z.enum(OVERVIEW_CARDS)).max(OVERVIEW_CARDS.length).nullable(),
    health: z.object({ kind: z.enum(PROJECT_HEALTH), note: z.string().trim().max(280).nullable() }).nullable(),
    /** P17: als Vorlage im Workspace anbieten */
    template: z.boolean(),
    sharing: z.enum(SHARING_MODES),
    /** P24 */
    phase: z.enum(PROJECT_PHASES),
    startDate: daySchema.nullable(),
    endDate: daySchema.nullable(),
    /** P25: Kürzel (Großbuchstaben); das alte bleibt als Verweis gültig */
    key: z
      .string()
      .trim()
      .transform((v) => v.toUpperCase())
      .pipe(z.string().regex(PROJECT_KEY_PATTERN, '2–6 Buchstaben oder Ziffern, am Anfang ein Buchstabe')),
  }),
});
export type UpdateProjectInput = z.infer<typeof updateProjectInput>;

export interface ProjectMemberDto {
  userId: string;
  name: string;
  email: string;
  role: z.infer<typeof projectRoleSchema>;
  version: number;
  /** N01: Linkgast – Name selbst angegeben, keine E-Mail (dann leer) */
  viaLink?: boolean;
  /** ADR 0063: Zugangsprofil eines Mitglieds (fehlt = uneingeschränkt) */
  profile?: { access: GuestAccess; onlyAssigned: boolean; ownCreated: boolean; material: boolean; intake?: 'all' | 'assigned' };
}

/**
 * ADR 0103: „Warum hat diese Person Zugriff?“ – alle Zugriffsquellen und Grenzen in Klartext (nur Leitung oder die Person
 * selbst). Administrative Rolle in der Arbeitsgruppe ist ausdrücklich KEIN inhaltlicher Zugriff.
 */
export type AccessReasonKind = 'workspace' | 'project' | 'team' | 'coordinate' | 'profile' | 'grant' | 'tasks' | 'until' | 'link' | 'role' | 'hidden';
export interface AccessExplanationDto {
  userId: string;
  name: string;
  role: z.infer<typeof projectRoleSchema>;
  /** ein Satz: was die Person sieht und tun kann */
  summary: string;
  reasons: { kind: AccessReasonKind; text: string }[];
}

/** Audit 0.36 F02: mehrere Personen auf einmal ins Projekt holen (z. B. ein Team) – einmalige Übernahme */
export const addProjectMembersInput = z.object({
  opId: opIdSchema,
  userIds: z.array(z.string().min(1).max(100)).min(1).max(100),
  role: z.enum(PROJECT_MEMBER_ROLES).default('member'),
  /** aus diesem Team übernommen: seine heutigen Mitglieder gelten als „gesehen“ (kein Hinweis) */
  teamId: uuidSchema.optional(),
});
export interface AddProjectMembersResultDto {
  added: ProjectMemberDto[];
  /** nicht übernommen (schon im Projekt, Gast, gesperrt, nicht in der Arbeitsgruppe) */
  skipped: { userId: string; reason: 'member' | 'guest' | 'suspended' | 'unknown' }[];
}

/** Audit 0.36 F02: Team aus Sicht eines Projekts – wer käme dazu, wer ist schon da, wer geht nicht */
export interface ProjectTeamDto {
  id: string;
  name: string;
  /** dem Projekt organisatorisch zugeordnet (Verwaltung → Teams) */
  assigned: boolean;
  /**
   * ADR 0169 (W4): `left` = früher über dieses Team übernommen, inzwischen nicht mehr im Team – bleibt Projektmitglied,
   * bis jemand den Zugriff ausdrücklich ändert
   */
  members: { userId: string; name: string; email: string; status: 'new' | 'member' | 'lead' | 'guest' | 'unavailable' | 'left' }[];
  /** zugeordnetes Team: neue Teammitglieder, die noch nicht im Projekt sind und nicht ausgeblendet wurden */
  unseen: string[];
}

/**
 * ADR 0169 (W4): Vorschau vor „Aus dem Projekt entfernen“ – nur Zahlen. Offene Zuständigkeiten werden gelöst (alleinige
 * Zuständigkeit = Aufgabe bleibt ohne Zuständige), Beiträge bleiben unter dem Namen erhalten, offene Abnahmen und
 * kommende Einsätze müssen neu vergeben werden.
 */
export interface MemberRemovalDto {
  openTasks: number;
  soleAssignee: number;
  pendingApprovals: number;
  upcomingShifts: number;
  comments: number;
  timeEntries: number;
  /** über ein Team übernommen (Teamname nur für die Leitung) */
  viaTeams: string[];
}

export const addProjectMemberInput = z.object({
  opId: opIdSchema,
  userId: z.string().min(1).max(100),
  role: z.enum(PROJECT_MEMBER_ROLES).default('member'),
});
export const updateProjectMemberInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ role: z.enum(PROJECT_MEMBER_ROLES), profile: memberProfileSchema.nullable() }),
});

/* ---------- Listen und Status ---------- */

export interface TaskListDto {
  id: string;
  projectId: string;
  /** Fortschritt im Listenkopf (P8): Aufgaben der obersten Ebene */
  openCount?: number;
  doneCount?: number;
  /** null = die automatisch angelegte erste Liste (Regel R6: ohne sichtbare Auswahl, solange sie die einzige ist) */
  name: string | null;
  position: string;
  /** Bereich (P14), null = Allgemein */
  areaId: string | null;
  version: number;
}

export interface TaskStatusDto {
  id: string;
  name: string;
  category: (typeof STATUS_CATEGORIES)[number];
  position: string;
  color: string | null;
  /** Hinweis-Grenze für offene Karten in der Spalte (P9), null = keine */
  wipLimit: number | null;
  version: number;
}

/** `id` optional vom Browser (Audit F04: Liste und erste Aufgabe in einem Zug über den Ausgangskorb) */
export const createListInput = z.object({ opId: opIdSchema, id: uuidSchema.optional(), name: nameSchema, areaId: uuidSchema.nullable().optional() });
export const updateListInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ name: nameSchema }),
});

/** Liste umsortieren: Nachbarn angeben, die Position rechnet der Server */
export const moveListInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  afterId: uuidSchema.nullable().optional(),
  beforeId: uuidSchema.nullable().optional(),
});

/**
 * Liste zusammenführen (Rückweg für „Liste hinzufügen“): alle Aufgaben wandern in Reihenfolge ans Ende der Ziel-Liste,
 * IDs und Links bleiben; die leere Liste wird ausgeblendet (archiviert), nichts wird gelöscht.
 */
export const mergeListInput = z.object({ opId: opIdSchema, targetListId: uuidSchema });

/** Board-Spalte (Status) anlegen; ohne Angabe vor der ersten Erledigt-Spalte */
export const createStatusInput = z.object({
  opId: opIdSchema,
  name: nameSchema,
  category: z.enum(STATUS_CATEGORIES).default('active'),
  afterId: uuidSchema.optional(),
});
export const updateStatusInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  // ADR 0126: Bedeutung der Spalte (offen / in Arbeit / abgeschlossen) – Wechsel über „abgeschlossen“ nur bei leerer Spalte
  patch: patchOf({ name: nameSchema, wipLimit: z.number().int().min(1).max(WIP_LIMIT_MAX).nullable(), category: z.enum(STATUS_CATEGORIES) }),
});
/** Board-Spalte verschieben (P9) */
export const moveStatusInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  afterId: uuidSchema.nullable().optional(),
  beforeId: uuidSchema.nullable().optional(),
});
/** Status entfernen: seine Aufgaben wechseln in den Ziel-Status (nichts verschwindet) */
/**
 * W-R05 (ADR 0173): Spalte entfernen prüft die Voraussetzungen des Ziels je Aufgabe. Gesperrte Aufgaben stoppen den
 * Vorgang, außer mit `mode: 'allowed'` (nur erlaubte verschieben – die Spalte bleibt mit den übrigen bestehen) oder
 * `override` (Leitung, nur Regeln mit erlaubter Ausnahme, protokolliert). `expectedPlan` aus der Vorschau.
 */
export const removeStatusInput = z.object({
  opId: opIdSchema,
  targetStatusId: uuidSchema,
  mode: z.enum(['all', 'allowed']).default('all'),
  override: z.object({ reason: z.string().trim().min(3).max(500) }).optional(),
  expectedPlan: z.string().max(64).optional(),
});
export interface StatusRemovalDto {
  targetStatusId: string;
  /** Aufgaben, die die Voraussetzungen des Ziels erfüllen (oder nur Hinweise auslösen) */
  allowed: number;
  /** gesperrte Aufgaben – Titel nur, wenn die Person die Aufgabe sieht */
  blocked: { id: string; title: string | null; problems: TransitionProblemDto[]; reason: string }[];
  /** alle Sperren erlauben eine Ausnahme durch die Leitung */
  exceptionAllowed: boolean;
  planKey: string;
}
export interface StatusRemovalResultDto {
  moved: number;
  /** in der Spalte verblieben (Teilverarbeitung) – die Spalte besteht dann weiter */
  kept: number;
  removed: boolean;
}

/* ---------- Aufgaben ---------- */

/** ADR 0115: Abschließen und nächsten Schritt anlegen (eine Operation) */
export const nextStepInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  title: z.string().trim().min(1).max(500),
  assigneeIds: z.array(z.string().min(1).max(100)).max(20).optional(),
  dueDate: daySchema.nullable().optional(),
});

/** ADR 0114: Fälligkeit relativ zu einem Termin */
export interface TaskBindingDto {
  eventId: string;
  offset: number;
  unit: 'days' | 'workdays';
}
export const taskBindingInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  /** null = Bindung lösen (Datum bleibt); sonst binden – mit `offset` oder dem jetzigen Abstand (ohne `offset`) */
  eventId: uuidSchema.nullable(),
  offset: z.number().int().min(-730).max(730).optional(),
  unit: z.enum(['days', 'workdays']).default('days'),
});

export interface TaskDto {
  id: string;
  /**
   * W-R06 (ADR 0173): nur in der Antwort eines Statuswechsels – Text der Hinweisregeln des Ziels („… empfiehlt: …“).
   * Nie in Ereignissen oder im Cache.
   */
  transitionHint?: string;
  projectId: string;
  /** P25: laufende Nummer im Projekt – Schlüssel = Projektkürzel + „-“ + Nummer */
  number: number;
  listId: string;
  /** Abschnitt in der Liste (P8) */
  sectionId: string | null;
  parentId: string | null;
  title: string;
  description: string | null;
  statusId: string;
  completedAt: string | null;
  startDate: string | null;
  dueDate: string | null;
  priority: number | null;
  assigneeIds: string[];
  /** Anzahl sichtbarer Kommentare und Anhänge (Paket P6) – Änderungen kommen als task.changed ohne neue Version */
  commentCount: number;
  attachmentCount: number;
  /** P8: Labels, Unteraufgaben und Checkliste (Zähler) */
  labelIds: string[];
  /** P19: Meilenstein (Raute) und Wiederholung – die nächste Aufgabe entsteht beim Erledigen */
  milestone: boolean;
  repeat: Repeat | null;
  /** die nächste Wiederholung gibt es schon (erneutes Erledigen legt keine weitere an) */
  repeatSpawned: boolean;
  /** P18: Werte eigener Felder (Feld-ID → Wert); fehlend = leer */
  fields: Record<string, FieldValue>;
  /** W03: Akzentfarbe (Palette) und Titelbild – Bild über GET /api/tasks/:id/cover?v=… (nie öffentlich) */
  accent: ColorValue | null;
  cover: TaskCoverDto | null;
  /** ADR 0114: Fälligkeit wandert mit dem Termin (`offset` Tage bzw. Werktage relativ zum Beginn); null = fest */
  binding?: TaskBindingDto | null;
  /** P26: wartet auf diese Aufgaben (IDs im selben Projekt) und wie viele davon noch offen sind */
  dependsOn: string[];
  blockers: number;
  subtaskCount: number;
  subtaskDone: number;
  checklistCount: number;
  checklistDone: number;
  /** P9: auf der Projektübersicht angeheftet (für alle im Projekt) – nicht zu verwechseln mit „Mein Tag“-Vormerken */
  onOverview: boolean;
  position: string;
  /**
   * 0.166 (Nachprüfung W-R02): eigene Reihenfolge im Board je Status, unabhängig von der Liste. `null` nur bei Altbestand
   * ohne Zuordnung – steht dann nach allen eingeordneten Karten in Listenreihenfolge.
   */
  boardPosition: string | null;
  version: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

/** W03: Titelbild setzen – Bild-Bytes (kleines Vorschaubild) im Rumpf, Angaben im Query */
export const setCoverQuery = z.object({
  opId: opIdSchema,
  fileId: uuidSchema,
  y: z.coerce.number().min(0).max(100),
  ratio: z.coerce.number().min(0.2).max(5),
  alt: z.string().trim().max(200).default(''),
});

export interface TaskCoverDto {
  fileId: string;
  y: number;
  alt: string;
  ratio: number;
  /** Fassung des Vorschaubilds (Cache-Schlüssel) */
  v: string;
}

const titleSchema = z.string().trim().min(1, 'Bitte einen Titel eingeben').max(TASK_TITLE_MAX);
const prioritySchema = z.number().int().min(1).max(4);
/** P19: alle N Tage/Wochen/Monate/Jahre, ab Termin oder ab dem Erledigen */
export const repeatSchema = z.object({ unit: z.enum(REPEAT_UNITS), interval: z.number().int().min(1).max(99), basis: z.enum(REPEAT_BASES) });

/** Regel R1: Eine Aufgabe braucht nur einen Titel; alles andere ist optional */
export const createTaskInput = z.object({
  opId: opIdSchema,
  title: titleSchema,
  listId: uuidSchema.optional(),
  /** Einfügen nach dieser Aufgabe (sonst ans Ende der Liste) */
  afterId: uuidSchema.optional(),
  /** z. B. beim Anlegen in einer Board-Spalte; sonst erster offener Status */
  statusId: uuidSchema.optional(),
  /** Abschnitt in der Liste (P8) */
  sectionId: uuidSchema.optional(),
  /** Unteraufgabe von … (P8, eine Ebene) */
  parentId: uuidSchema.optional(),
  dueDate: daySchema.optional(),
  assigneeIds: z.array(z.string().min(1).max(100)).max(20).optional(),
  /** W05: Erfassung in der Tabelle – aus dem Filter eindeutig ableitbare Werte gleich mit anlegen */
  priority: prioritySchema.optional(),
  labelIds: z.array(uuidSchema).max(20).optional(),
  /** Aus Mein Tag angelegt: für die anlegende Person vormerken (Paket P7) */
  myDay: z.boolean().optional(),
})
  // Etappe D: unbekannte Felder (z. B. `repeat`) ablehnen statt still ignorieren – sie werden per PATCH gesetzt
  .strict();
export type CreateTaskInput = z.infer<typeof createTaskInput>;

/** Mehrzeilen-Einfügen: mehrere Titel in einer Operation, in Reihenfolge ans Ende */
export const createTasksBulkInput = z.object({
  opId: opIdSchema,
  titles: z.array(titleSchema).min(1).max(BULK_MAX),
  listId: uuidSchema.optional(),
});

/**
 * 0.63 „Mehrere Aufgaben auf einmal“: Plan aus dem Browser (Leser in @werft/domain) für EIN Projekt. Spalten = Status
 * (per Namen zugeordnet, fehlende neu, wenn `useSourceColumns`); Labels per Namen, fehlende neu. Personen nur
 * Projektmitglieder (`memberMap`).
 */
// Nur Server und Tests brauchen das Schema: als „pure“ markiert, damit es nicht im Startpaket des Browsers landet
const importKey = /* @__PURE__ */ z.string().min(1).max(64);
export const importTasksInput = /* @__PURE__ */ (() =>
  z
  .object({
    opId: opIdSchema,
    listId: uuidSchema.optional(),
    /** Zielstatus (Board-Spalte); ohne Angabe der erste offene */
    statusId: uuidSchema.optional(),
    sectionId: uuidSchema.optional(),
    useSourceColumns: z.boolean().default(false),
    source: z.enum(['paste', 'text', 'csv', 'xlsx', 'planner', 'asana', 'jira', 'todoist', 'ticktick']),
    memberMap: z.record(importKey, uuidSchema.nullable()).default({}),
    plan: z.object({
      columns: z.array(z.object({ key: importKey, title: z.string().trim().min(1).max(200) })).max(50),
      labels: z.array(z.object({ key: importKey, name: z.string().max(40), color: colorValueSchema })).max(50),
      members: z.array(z.object({ key: importKey, name: z.string().trim().min(1).max(200) })).max(100).default([]),
      tasks: z
        .array(
          z.object({
            key: importKey,
            columnKey: importKey,
            title: titleSchema,
            description: z.string().max(20_000).default(''),
            labelKeys: z.array(importKey).max(20).default([]),
            memberKeys: z.array(importKey).max(20).default([]),
            dueDate: daySchema.nullable().default(null),
            startDate: daySchema.nullable().default(null),
            priority: prioritySchema.nullable().default(null),
            completed: z.boolean().default(false),
            checklist: z.array(z.object({ text: z.string().trim().min(1).max(1000), done: z.boolean() })).max(100).default([]),
          }),
        )
        .min(1)
        .max(BULK_MAX),
    }),
  })
  .strict())();
export type ImportTasksInput = z.infer<typeof importTasksInput>;

export interface ImportTasksResult {
  items: TaskDto[];
  statuses: number;
  labels: number;
  /** Gäste legen keine Status/Labels an – so viele Angaben blieben weg */
  skipped: number;
  /** ADR 0170: Aufgaben, deren Zielstatus gesperrt war – im ersten offenen Status angelegt */
  held?: number;
}

/**
 * ADR 0170 (W3): Voraussetzungen für den Eintritt in einen Status. `ref` bei `field`: `dueDate`, `description`,
 * `priority` oder die ID eines eigenen Felds. Nur die Projektleitung verwaltet sie.
 */
export const STATUS_RULE_KINDS = ['assignee', 'predecessors', 'approval', 'field'] as const;
export type StatusRuleKind = (typeof STATUS_RULE_KINDS)[number];
export const BUILTIN_RULE_FIELDS = ['dueDate', 'description', 'priority'] as const;
/**
 * Nachprüfung W-R04 (ADR 0173): Bezug der Regel „Abnahme erteilt“ – die Aufgabe selbst (Vorgabe), eine mit ihr
 * verknüpfte Datei oder eine verknüpfte Seite. Gültig ist nur eine Abnahme, deren geprüfter Stand noch der aktuelle ist.
 */
export const APPROVAL_RULE_REFS = ['task', 'file', 'page'] as const;
export type ApprovalRuleRef = (typeof APPROVAL_RULE_REFS)[number];
export const statusRuleInput = z.object({
  opId: opIdSchema,
  id: uuidSchema,
  statusId: uuidSchema,
  kind: z.enum(STATUS_RULE_KINDS),
  ref: z.string().max(64).nullable().optional(),
  mode: z.enum(['hint', 'block']).default('block'),
  exception: z.boolean().default(false),
});
export const updateStatusRuleInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ mode: z.enum(['hint', 'block']), exception: z.boolean() }),
});
export interface StatusRuleDto {
  id: string;
  statusId: string;
  kind: StatusRuleKind;
  ref: string | null;
  mode: 'hint' | 'block';
  exception: boolean;
  version: number;
  /** Bezug fehlt (Feld gelöscht) – eine Sperrregel sperrt dann, bis sie repariert ist */
  broken: boolean;
}
/** Eine nicht erfüllte Voraussetzung – ohne Titel oder Zahlen verborgener Inhalte (`hidden`) */
export interface TransitionProblemDto {
  ruleId: string;
  kind: StatusRuleKind | 'broken';
  ref: string | null;
  mode: 'hint' | 'block';
  exception: boolean;
  /** Vorgänger, die die Person nicht sieht, sind offen – nur als allgemeines Hindernis */
  hidden?: boolean;
  /** W-R04: Es gibt eine erteilte Abnahme, aber der Inhalt hat sich seitdem geändert – erneut abnehmen lassen */
  stale?: boolean;
}
export interface TaskTransitionsDto {
  statuses: { statusId: string; problems: TransitionProblemDto[] }[];
}

export const updateTaskInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  /** ADR 0170: Sperre mit Begründung übergehen – nur Leitung, nur Regeln mit erlaubter Ausnahme */
  override: z.object({ reason: z.string().trim().min(3).max(500) }).optional(),
  patch: patchOf({
    title: titleSchema,
    description: z.string().max(100_000).nullable(),
    statusId: uuidSchema,
    /** Abhaken: setzt den ersten Erledigt-Status bzw. zurück auf den ersten offenen */
    completed: z.boolean(),
    startDate: daySchema.nullable(),
    dueDate: daySchema.nullable(),
    priority: prioritySchema.nullable(),
    milestone: z.boolean(),
    repeat: repeatSchema.nullable(),
    /** W03: Akzentfarbe aus der geprüften Palette (null = keine) */
    accent: colorValueSchema.nullable(),
  }),
});
export type UpdateTaskInput = z.infer<typeof updateTaskInput>;

/** Zuständige als Delta (wie KaBa): parallele Änderungen verschiedener Personen überschreiben sich nicht */
/** P26: Abhängigkeiten als Delta („wartet auf“) – höchstens DEPENDENCIES_MAX je Aufgabe */
export const DEPENDENCIES_MAX = 20;
export const changeDependenciesInput = z.object({
  opId: opIdSchema,
  add: z.array(uuidSchema).max(DEPENDENCIES_MAX).default([]),
  remove: z.array(uuidSchema).max(DEPENDENCIES_MAX).default([]),
});

/** P26: Aufgabe in einer Abhängigkeit (für die Details; nur Sichtbares) */
export interface DependencyTaskDto {
  id: string;
  number: number;
  title: string;
  completed: boolean;
  startDate: string | null;
  dueDate: string | null;
}
export interface DependenciesDto {
  /** worauf diese Aufgabe wartet */
  waitsFor: DependencyTaskDto[];
  /** wer auf diese Aufgabe wartet */
  blocks: DependencyTaskDto[];
}

export const changeAssigneesInput = z.object({
  opId: opIdSchema,
  add: z.array(z.string().min(1).max(100)).max(20).default([]),
  remove: z.array(z.string().min(1).max(100)).max(20).default([]),
});

/** Verschieben: Ziel-Liste und Nachbarn; die endgültige Position rechnet der Server */
/**
 * 0.166 (Nachprüfung W-R01/W-R03): Karte im Board ablegen – Zielstatus und Stelle in EINEM Vorgang. `afterId` = Karte
 * direkt darüber, `beforeId` = direkt darunter (beide im Zielstatus, ohne die bewegte Karte). Abgelehnt = nichts geändert.
 */
export const boardMoveInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  statusId: uuidSchema,
  afterId: uuidSchema.nullable().optional(),
  beforeId: uuidSchema.nullable().optional(),
  /** ADR 0170: Ausnahme wie beim PATCH – nur Leitung */
  override: z.object({ reason: z.string().trim().min(3).max(500) }).optional(),
});
export type BoardMoveInput = z.infer<typeof boardMoveInput>;

export const moveTaskInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  listId: uuidSchema,
  /** Abschnitt im Ziel (null = ohne); weggelassen = beibehalten, beim Listenwechsel ohne */
  sectionId: uuidSchema.nullable().optional(),
  afterId: uuidSchema.nullable().optional(),
  beforeId: uuidSchema.nullable().optional(),
});
export type MoveTaskInput = z.infer<typeof moveTaskInput>;

/** Stand eines Projekts zum Erstladen: Struktur + Cursor (Aufgaben kommen seitenweise über /tasks) */
export interface ProjectSnapshotDto {
  project: ProjectDto;
  /** Bereiche (P14) mit ihren Teams; leer = keine Bereichsleiste */
  areas: AreaDto[];
  lists: TaskListDto[];
  sections: SectionDto[];
  labels: LabelDto[];
  /** Eigene Felder (P18); leer = nirgends sichtbar */
  fields: CustomFieldDto[];
  statuses: TaskStatusDto[];
  members: ProjectMemberDto[];
  /** P21: frühere Mitglieder und Gäste (nur Namen) */
  formerMembers: FormerMemberDto[];
  cursor: number;
}

export interface TaskPageDto {
  items: TaskDto[];
  /** opaker Cursor für die nächste Seite; null = Ende */
  next: string | null;
  cursor: number;
}

/* ---------- P8: Abschnitte, Labels, Checklisten ---------- */

export interface SectionDto {
  id: string;
  listId: string;
  name: string;
  position: string;
  version: number;
}

export const createSectionInput = z.object({ opId: opIdSchema, listId: uuidSchema, name: nameSchema });
export const updateSectionInput = z.object({ opId: opIdSchema, expectedVersion: z.number().int().nonnegative(), patch: patchOf({ name: nameSchema }) });
export const moveSectionInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  afterId: uuidSchema.nullable().optional(),
  beforeId: uuidSchema.nullable().optional(),
});

export interface LabelDto {
  id: string;
  name: string;
  color: ColorValue;
  version: number;
}

export const createLabelInput = z.object({ opId: opIdSchema, name: z.string().trim().min(1).max(40), color: colorValueSchema });
export const updateLabelInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ name: z.string().trim().min(1).max(40), color: colorValueSchema }),
});
export const changeLabelsInput = z.object({
  opId: opIdSchema,
  add: z.array(uuidSchema).max(20).default([]),
  remove: z.array(uuidSchema).max(20).default([]),
});

export interface ChecklistItemDto {
  id: string;
  taskId: string;
  text: string;
  done: boolean;
  position: string;
  version: number;
}

const checklistText = z.string().trim().min(1).max(500);
export const createChecklistInput = z.object({ opId: opIdSchema, text: checklistText, afterId: uuidSchema.optional() });
export const updateChecklistInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().nonnegative(),
  patch: patchOf({ text: checklistText, done: z.boolean() }),
});
export const moveChecklistInput = z.object({ opId: opIdSchema, afterId: uuidSchema.nullable().optional(), beforeId: uuidSchema.nullable().optional() });

/* ---------- P9: Projektübersicht ---------- */

export interface ProjectOverviewDto {
  /** Offene Aufgaben mit Termin heute bis in 14 Tagen, nach Termin, höchstens 5 */
  upcoming: TaskDto[];
  /** Überfällige offene Aufgaben, zuletzt fällig gewordene zuerst, höchstens 3 (Anzahl in `overdue`) */
  overdueTasks: TaskDto[];
  overdue: number;
  /** Angeheftete Aufgaben, zuletzt angeheftet zuerst */
  pinned: TaskDto[];
  /** Audit F07: angeheftete Seiten, Dateien und Themen (nur sichtbare, nicht im Papierkorb) */
  items: PinnedItemDto[];
  open: number;
  done: number;
  /** Audit 0.36 F08: nächster offener Meilenstein (auch nach dem 14-Tage-Fenster; mehrere am selben Tag), sonst leer */
  milestones: TaskDto[];
  /** Audit 0.36 F07: „Braucht Aufmerksamkeit“ – nur, was stockt; leere Gruppen zeigt die Oberfläche nicht */
  attention: OverviewAttentionDto;
}

export interface OverviewAttentionDto {
  /** offene Aufgaben mit offenem Vorgänger (höchstens 3, Anzahl in `blocked`) */
  blocked: number;
  blockedTasks: TaskDto[];
  /** Briefing AF15: worauf eine blockierte Aufgabe wartet (erster offener, sichtbarer Vorgänger) */
  waitingOn: { taskId: string; id: string; title: string }[];
  /** dringend (Priorität dringend/hoch oder in ≤ 3 Tagen fällig/überfällig) und ohne Zuständige */
  unassigned: number;
  unassignedTasks: TaskDto[];
  /** Themen mit ausstehender Entscheidung (nur mit Gesprächen) */
  decisions: number;
  decisionTopics: { id: string; title: string }[];
  /** Etappe C: offene Abnahmen (nur sichtbare Inhalte) */
  approvals: number;
  approvalItems: { id: string; kind: 'task' | 'page' | 'file'; targetId: string; title: string; approverId: string | null; folderId: string | null }[];
  /** Abnahme 0.58 F03: offene Datei-Rückmeldungen (Anzahl) und die Dateien mit der jüngsten offenen Rückmeldung */
  feedback: number;
  feedbackFiles: { id: string; title: string; folderId: string | null; n: number }[];
  /** ADR 0064: neue Vorgänge im Eingang (nur wer ihn bearbeiten darf) */
  intake?: number;
}

export const pinTaskInput = z.object({ opId: opIdSchema, on: z.boolean() });

/**
 * Audit F10: Rückverweise einer Aufgabe – woher sie kommt (Thema, Antwort, Chat) und welche Seiten auf sie verlinken.
 * Nur Sichtbares aus zugeschalteten Werkzeugen.
 */
export interface TaskLinkDto {
  kind: 'topic' | 'post' | 'message' | 'page' | 'task' | 'file';
  id: string;
  /** Thema (bei Antworten: das Thema der Antwort) */
  topicId: string | null;
  title: string;
}

/**
 * Audit F10: Verknüpfungen zwischen Inhalten verschiedener Werkzeuge – eine gemeinsame Aktion „Verknüpfen“.
 * Datei ↔ Aufgabe bleibt `file_link` (Ablage, ADR 0014); alle anderen Paare stehen in `content_link`.
 */
export const LINK_KINDS = ['task', 'page', 'file', 'topic', 'intake', 'event', 'decision', 'goal', 'risk', 'budget', 'contact', 'shift', 'folder'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];
export const linkRefSchema = z.object({ kind: z.enum(LINK_KINDS), id: uuidSchema });
/** ADR 0122: persönlicher Favorit in der Lobby */
export const favoriteInput = z.object({ opId: opIdSchema, on: z.boolean() });
/** ADR 0124: persönliche Lobby-Ordner */
export const LOBBY_FOLDER_DEPTH = 3;
export const createLobbyFolderInput = z.object({ opId: opIdSchema, id: uuidSchema, name: z.string().trim().min(1).max(80), parentId: uuidSchema.nullable() });
export const updateLobbyFolderInput = z.object({ opId: opIdSchema, name: z.string().trim().min(1).max(80).optional(), parentId: uuidSchema.nullable().optional() });
export const placeProjectInput = z.object({ opId: opIdSchema, folderId: uuidSchema.nullable() });
/**
 * ADR 0129 (Nachprüfung 0.111 U05): lebende Einbettungen in Notizen. Die Seite speichert nur Art und Verweis; jeder
 * Betrachter wird beim Anzeigen erneut berechtigt. Kein Titel, keine Summe im Seiteninhalt selbst.
 */
export const EMBED_KINDS = ['tasks', 'event', 'budget', 'file'] as const;
export type EmbedKind = (typeof EMBED_KINDS)[number];
export type EmbedDto =
  | { kind: 'tasks'; title: string; open: number; done: number; items: { id: string; number: number; title: string; done: boolean; dueDate: string | null }[]; more: number }
  | { kind: 'event'; id: string; title: string; state: string; allDay: boolean; startDay: string | null; endDay: string | null; startTime: string | null; endTime: string | null; location: string | null; agenda: number }
  | { kind: 'budget'; currency: string; planCents: number; committedCents: number; actualCents: number; availableCents: number; lines: number }
  | { kind: 'file'; id: string; name: string; version: number; updatedAt: string; approval: 'pending' | 'approved' | 'changes' | null; approvalVersion: number | null };

/** ADR 0125: Papierkorb für Projekte */
export const trashProjectInput = z.object({ opId: opIdSchema, expectedVersion: z.number().int().nonnegative() });
export const purgeProjectInput = z.object({ opId: opIdSchema, confirmName: z.string().max(200) });
export interface TrashedProjectDto {
  id: string;
  name: string;
  color: string | null;
  trashedAt: string;
  trashedBy: string | null;
  /** Kalendertag, ab dem das Projekt endgültig gelöscht wird */
  purgeOn: string;
  version: number;
}
/** Was beim endgültigen Löschen verloren geht und was gerade noch läuft (Vorschau, rein lesend) */
export interface DeletePreviewDto {
  name: string;
  trashed: boolean;
  purgeOn: string | null;
  content: { tasks: number; pages: number; files: number; bytes: number; topics: number; events: number; contacts: number; intake: number; timeEntries: number; budgetLines: number };
  people: { members: number; guests: number; linkGuests: number };
  running: { shareLinks: number; projectLinks: number; forms: number; rules: number; webhooks: number; openIntake: number; pendingApprovals: number; calendarFeeds: number };
}
export interface LobbyFolderDto {
  id: string;
  parentId: string | null;
  name: string;
  position: string;
}
export const setLinkInput = z.object({ opId: opIdSchema, from: linkRefSchema, to: linkRefSchema, on: z.boolean() });

export interface LinkedItemDto {
  kind: LinkKind;
  id: string;
  title: string;
  /** Aufgabe: Schlüsselnummer; Datei: Ordner (für die Adresse) */
  number: number | null;
  folderId: string | null;
  /** erledigt (Aufgabe) bzw. gelöst (Thema) */
  done: boolean;
  /** Audit 0.36 F09: Thema mit festgehaltener Entscheidung – deren Text (gekürzt) */
  decision?: string | null;
  /** ADR 0072: Termin – erster Tag (fehlt bei Entwürfen) */
  day?: string | null;
  /**
   * Projektinsel W11: Beziehung aus Sicht des betrachteten Inhalts – `origin` „Entstanden aus“, `derived` „Daraus
   * entstanden“, `review` „Zur Prüfung“ (Datei mit offener Abnahme); fehlt = einfach verknüpft
   */
  /** Gesamtabnahme 0.58: `feedback` = Rückmeldung einer bestehenden Aufgabe zugeordnet, `handles` = in dieser Aufgabe bearbeitet */
  /** ADR 0115: `next` „Nächster Schritt“ / `nextOf` „Nächster Schritt zu“, `prep` „Vorbereitung“ / `prepFor` „Vorbereitung für“ */
  /** ADR 0167 (W1): `needs` „Wartet auf“ / `neededBy` „Wird gebraucht für“ (Abhängigkeit), `bound` „Frist hängt an diesem Termin“ / `boundTo` „Frist hängt an“ */
  relation?: 'origin' | 'derived' | 'review' | 'feedback' | 'handles' | 'next' | 'nextOf' | 'prep' | 'prepFor' | 'cost' | 'costOf' | 'contact' | 'contactFor' | 'shift' | 'shiftFor' | 'material' | 'materialFor' | 'needs' | 'neededBy' | 'bound' | 'boundTo' | null;
  /** W11: Anlass genauer – Rückmeldung zu Fassung n */
  viaFeedback?: number | null;
  /** W11: Datei – aktuelle Fassung und Abnahmestand (nur sichtbare Dateien) */
  version?: number | null;
  approval?: { state: 'pending' | 'approved' | 'changes'; versionNumber: number | null } | null;
  /** W11: Verbindung entsteht aus dem Inhalt selbst (Aufgabe aus Gespräch) – lässt sich hier nicht lösen */
  fixed?: boolean;
  /** ADR 0135: Kontextrollen dieser Person an diesem Anlass (nur mit Personendaten-Recht und Sicht auf beide Enden) */
  roles?: LinkRoleDto[];
}

/**
 * ADR 0135 (Briefing Paket D): Rolle und Beitrag einer Person an einem Anlass – gespeichert an der Beziehung, nicht am
 * Kontakt. Vorschläge plus freie Bezeichnung, mehrere Rollen je Paar. Rein beschreibend: keine Rechte, kein Konto,
 * keine Einladung. `contactId` null = offene Rolle (z. B. aus einer Vorlage), die noch einer Person zugeordnet wird.
 */
export const ROLE_SUGGESTIONS = ['Ansprechperson', 'führt aus', 'liefert', 'prüft', 'koordiniert'] as const;
export const ROLE_TARGET_KINDS = ['task', 'event', 'shift', 'page', 'file', 'topic', 'decision', 'goal', 'risk', 'budget', 'intake', 'folder'] as const;
export type RoleTargetKind = (typeof ROLE_TARGET_KINDS)[number];
export interface LinkRoleDto {
  id: string;
  contactId: string | null;
  target: { kind: RoleTargetKind; id: string };
  role: string;
  contribution: string | null;
  version: number;
}
const roleText = z.string().trim().min(1).max(40);
const contributionText = z.string().trim().max(500);
export const createLinkRoleInput = z.object({
  opId: opIdSchema,
  id: uuidSchema,
  contactId: uuidSchema.nullable(),
  target: z.object({ kind: z.enum(ROLE_TARGET_KINDS), id: uuidSchema }),
  role: roleText,
  contribution: contributionText.nullable().optional(),
});
export const updateLinkRoleInput = z.object({
  opId: opIdSchema,
  expectedVersion: z.number().int().positive(),
  patch: patchOf({ role: roleText, contribution: contributionText.nullable(), contactId: uuidSchema }),
});
export const deleteLinkRoleInput = z.object({ opId: opIdSchema });

/** Audit F07: angeheftete Inhalte anderer Werkzeuge auf der Übersicht (Aufgaben haben ihr eigenes Merkmal) */
export const PIN_KINDS = ['page', 'file', 'topic'] as const;
export type PinKind = (typeof PIN_KINDS)[number];
export const setPinInput = z.object({ opId: opIdSchema, kind: z.enum(PIN_KINDS), targetId: uuidSchema, on: z.boolean() });

export interface PinnedItemDto {
  kind: PinKind;
  id: string;
  title: string;
  /** Datei: Ordner (für die Vorschau-Adresse) */
  folderId: string | null;
  pinnedAt: string;
}

/** W13 (Grundlage): „Deine nächsten Schritte“ im Projekt – eigene offene Aufgaben und Abnahmen, die auf dich warten */
export interface ForMeDto {
  tasks: { id: string; title: string; dueDate: string | null }[];
  approvals: { id: string; kind: 'task' | 'page' | 'file'; targetId: string; title: string; folderId: string | null }[];
}

/**
 * Projektinsel W12: begründeter nächster Schritt in der Übersicht (und im Gast-Einstieg). Jede Art hat eine eigene
 * Aussage mit Anlass, das Ziel ist ein sichtbarer Inhalt, `key` bleibt gleich, solange die Lage dieselbe ist
 * (persönliches Zurückstellen). `rank`: 0 eigene ausstehende Handlung, 1 Blockade/Startklar, 2 fällig/Hinweis.
 */
export const NEXT_STEP_KINDS = ['approval', 'decide', 'feedback', 'reply', 'ready', 'version', 'unassigned', 'decision', 'due'] as const;
export type NextStepKind = (typeof NEXT_STEP_KINDS)[number];
export interface NextStepDto {
  key: string;
  kind: NextStepKind;
  rank: number;
  target: { kind: 'task' | 'page' | 'file' | 'topic' | 'decision'; id: string; title: string; folderId: string | null };
  /** Anzahl (offene Rückmeldungen) */
  n?: number;
  /** Fassung, auf die sich eine Abnahme bezog, und aktuelle Fassung */
  version?: number | null;
  current?: number | null;
  /** Termin (fällig) */
  due?: string | null;
  /** darf die Person die passende Handlung direkt ausführen (z. B. Zuständigkeit vergeben) */
  canAct: boolean;
  /**
   * Abnahme 0.58 F03: weitere Anlässe desselben Inhalts, zu einer Zeile zusammengefasst („heute fällig · noch
   * niemand zuständig“). Die führende Aussage steht in `kind`.
   */
  also?: NextStepKind[];
}
export interface NextStepsDto {
  items: NextStepDto[];
}

/**
 * ADR 0123 (Nachprüfung 0.111 U02): Aufgaben in ein anderes Projekt verschieben – mit Vorschau (`dryRun`). Höchstens 200
 * Aufgaben (mit Unteraufgaben) in einer Operation, ganz oder gar nicht.
 */
export const transferTasksInput = z.object({
  opId: opIdSchema,
  taskIds: z.array(uuidSchema).min(1).max(200),
  targetProjectId: uuidSchema,
  targetListId: uuidSchema,
  /** Ziel ist für mehr Personen sichtbar als die Quelle – nur mit ausdrücklicher Bestätigung */
  allowWider: z.boolean().optional(),
  /** 0.131 (R06): Werte ohne Gegenstück im Ziel gehen verloren – nur mit ausdrücklicher Bestätigung */
  allowLoss: z.boolean().optional(),
  /** 0.131: Kennung der gelesenen Vorschau – hat sich der Plan seitdem geändert, 409 `plan_changed` mit neuer Vorschau */
  expectedPlan: z.string().max(80).optional(),
  dryRun: z.boolean().optional(),
});
export type TransferTasksInput = z.infer<typeof transferTasksInput>;

export interface TransferPreviewDto {
  /** Aufgaben, die mitgehen (oberste Ebene), mit Unteraufgaben-Anzahl */
  /** Status mit Bedeutung (R05): der Erledigt-Zustand bleibt immer erhalten */
  tasks: { id: string; title: string; subtasks: number; status: { from: string; to: string; fromCategory: 'open' | 'active' | 'done'; toCategory: 'open' | 'active' | 'done' } }[];
  /** nicht verschiebbar (z. B. offene Abnahme) – nichts wird verschoben, solange es Einträge gibt; unsichtbare Aufgaben → 404 */
  blocked: { id: string; title: string; reason: 'approval' | 'subtask' }[];
  /** Kennung dieses Plans – beim Ausführen als `expectedPlan` mitgeben */
  planKey: string;
  /** Ziel ist offener als die Quelle (vertraulicher Bereich → allgemeiner Bereich) */
  wider: boolean;
  /** was mitgeht */
  carried: { subtasks: number; comments: number; attachments: number; checklist: number };
  /** was zugeordnet wird bzw. entfällt – nie still */
  labels: { kept: string[]; dropped: string[] };
  fields: { kept: string[]; dropped: string[] };
  /** R06: einzelne Werte, die im Ziel nicht übernommen werden (Option fehlt/mehrdeutig, Person ohne Zugang) */
  lostValues: { task: string; field: string; value: string; reason: 'option' | 'ambiguous' | 'person' }[];
  assignees: { kept: number; dropped: string[] };
  dropped: { dependencies: number; links: number; fileLinks: number; guestGrants: number };
  /** bleibt im bisherigen Projekt (Geschäftsdaten), nur ohne Aufgabenbezug */
  stays: { timeEntries: number; budget: number };
  /** R04: laufende Timer an den Aufgaben – werden beim Verschieben beendet, die Zeit bleibt als Entwurf (Namen nur für die Leitung) */
  runningTimers: { count: number; people: string[] | null };
  /** N01 (0.140): so viele Personen sehen die Aufgaben im Ziel, die sie vorher nicht gesehen haben (Namen nie) */
  newReaders: number;
}

export interface TransferResultDto {
  moved: number;
  /** neue Schlüssel im Zielprojekt (alter Schlüssel bleibt auflösbar) */
  tasks: { id: string; key: string }[];
}
